import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const AssignPickerForm = NiceModal.create(({ id }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					waveID: yup.string().nullable().required(),
					assignedPickerID: yup.string().nullable().required()
				})
				.required(),
		[]
	);
	const dispatch = useDispatch();

	const [tableData, setTableData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const { showError, showSuccess } = useAlert();

	const axios = useAxiosPrivate();
	const modal = useModal();

	const defaultColSpan = {
		md: 6
	};
	const whUserAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);
	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const { handleSubmit, setValue, watch, reset, getValues } = formMethods;

	const handleCloseModal = () => modal.remove();

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			await axios.post(ADVANCE_WMS_MODULE.ASSIGN_PICKER, {
				waveID: data.waveID,
				assignedPickerID: data.assignedPickerID
			});
			dispatch(updateDynamicListData(DYNAMICLISTS.WAVE_LIST));
			showSuccess();
			handleCloseModal();
		} catch (error) {
			showError();
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};
	const fillForm = async () => {
		setIsLoading(true);
		try {
			const { data } = await axios.post(ADVANCE_WMS_MODULE.GET_WAVE_BY_ID, { id });

			reset({
				...data,
				waveID: getValues('waveID')
			});
			data.waveItems && setTableData(data.waveItems);
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (id) setValue('waveID', id, { shouldValidate: true });
		id && fillForm();
	}, [id]);

	const tableColumns = useMemo(
		() => [
			{
				header: 'SKU',
				accessorKey: 'sku',
				size: 50
			},

			{
				header: 'qtyTobePicked',
				accessorKey: 'qtyTobePicked',
				size: 50
			},
			{
				header: 'qtyPicked',
				accessorKey: 'qtyPicked',
				size: 50
			},
			{
				header: 'lotNumber',
				accessorKey: 'lotNumber',
				size: 50
			},
			{
				header: 'expiryDate',
				accessorKey: 'expiryDate',
				size: 50
			},
			{
				header: 'binLocations',
				accessorKey: 'binLocations',
				size: 50
			}
		],
		[]
	);

	return (
		<Modal fullwidth>
			<ModalTitle>
				<Typography variant="p1">assignPicker</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="waveNumber" isLoading={isLoading} required viewMode>
						<StringInput name="waveNumber" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="assignedPicker" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSE_USERS}
							name="assignedPickerID"
							value={watch('assignedPickerID')}
							setValue={(value) => {
								setValue('assignedPickerID', value?.ID ?? null, {
									shouldDirty: true,
									shouldValidate: true
								});
							}}
							columns={whUserAutocompleteColumns}
							selectionLabelAccessor={['name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="startDate" isLoading={isLoading} required viewMode>
						<StringInput name="startDate" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="completeDate" isLoading={isLoading} required viewMode>
						<StringInput name="completionDate" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="orderIds" isLoading={isLoading} required viewMode>
						<StringInput name="orderIds" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required viewMode>
						<Status value={watch('status')} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="binLocations" isLoading={isLoading} required viewMode>
						<StringInput name="binLocations" disabled />
					</FormItem>
					<Table loading={isLoading} data={tableData} columns={tableColumns} />
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label={'save'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});
export default AssignPickerForm;
